import React from 'react';
import FormLabel from '@material-ui/core/FormLabel';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import PropTypes from 'prop-types';
import Utils from '../../../../../utils';
import styles from './styles/chipselectfield.scss';

export default class ChipSelectField extends React.Component {
    render() {
        const {
            id,
            title,
            options,
            emptyValue,
            error,
            errorText,
            helperText,
            required,
            disabled,
            multiple,
            inputValue,
            onBlur,
            onFocus,
            valueOptions,
            uiSchema,
            autoSelectSingleResult,
            formControlComponent
        } = this.props;

        const { classes = {} } = uiSchema;
        const onChange = ({ target: { value } }) => {

            if (!multiple) {
                return this.props.onChange(id, value === '' ? emptyValue : value);
            }

            // if no values were selected, return the first value as an array
            if (!Utils.isArray(inputValue)) {
                return this.props.onChange(id, [value]);
            }

            const selectedValues = [...inputValue];

            if (~selectedValues.indexOf(value)) {
                selectedValues.splice(selectedValues.indexOf(value), 1);
            } else {
                selectedValues.push(value);
            }

            return this.props.onChange(id, selectedValues);
        };

        const grouped = !!(options.groups && options.groups.length);
        const chips = grouped ? {} : [];

        valueOptions.forEach((valueOption, i) => {
            const { key, label, group } = valueOption;
            let checked = false;
            const optionDisabled = disabled ? disabled : valueOption.disabled;
            const type = multiple ? 'checkbox' : 'radio';

            if (inputValue) {
                if ((multiple && Utils.isArray(inputValue) && ~inputValue.indexOf(key)) ||
                    inputValue === key) {
                    checked = true;
                }
            }

            let chipContainerClass = styles.chipContainer;
            let chipLabelClass = styles.chipLabel;

            if (classes.chipContainer) {
                chipContainerClass += ` ${classes.chipContainer}`;
            }

            const chipLabelClasses = [
                classes.labelContainer,
                classes.chipLabelSelected,
                classes.chipLabelDisabled
            ];

            chipLabelClasses.forEach(styleClass => {
                if (styleClass) {
                    chipLabelClass += ` ${styleClass}`;
                }
            });

            // select option if only one available
            if (autoSelectSingleResult) {
                checked = true;
            }

            if (checked) {
                chipLabelClass += ` ${styles.chipLabelSelected}`;
            }

            if (optionDisabled) {
                chipLabelClass += ` ${styles.chipLabelDisabled}`;
            }

            let chipArray = chips;

            // determine which group to add this option to
            if (grouped) {
                if (!group) {
                    return;
                }

                if (!chips[group]) {
                    chips[group] = [];
                }
                chipArray = chips[group];
            }

            chipArray.push(
                <div key={ `checkable-option-${id}-${i}` } className={ chipContainerClass }>
                    <label
                        htmlFor={ `${id}-${i}` }
                        className={ chipLabelClass }
                        onBlur={ onBlur && (() => onBlur({ id, value: key })) }
                        onFocus={ onFocus && (() => onFocus({ id, value: key })) }
                    >
                        <input
                            className={ `${styles.input} ${classes.input || ''}` }
                            type={type}
                            id={ `${id}-${i}` }
                            name={ id }
                            value={ key }
                            checked={ checked }
                            onChange={ onChange }
                            disabled={ optionDisabled }
                            data-cy={`select-${id}-${label}-${type}-field`}
                        />
                        <span className={ classes.label || '' }>{ label }</span>
                    </label>
                </div>
            );
        });

        let renderChips = [];

        if (grouped) {
            let labelContainerClass = styles.groupLabelContainer;
            let groupChipContainerClass = '';

            if (options.groupLabelPlacement === 'top') {
                labelContainerClass += ` ${styles.groupLabelTop}`;
            } else if (options.groupLabelPlacement === 'left') {
                labelContainerClass += ` ${styles.groupLabelLeft}`;
                groupChipContainerClass = styles.groupInlineChipContainer;
            }

            if (classes.groupLabelContainer) {
                labelContainerClass += ` ${classes.groupLabelContainer}`;
            }

            if (classes.groupChipContainer) {
                groupChipContainerClass = groupChipContainerClass
                    ? `${groupChipContainerClass} ${classes.groupChipContainer}` : classes.groupChipContainer;
            }

            options.groups.forEach(group => {
                let groupLabel = '';

                for (let i = 0; i < options.groups.length; i++) {
                    if (options.groups[i].key === group.key) {
                        groupLabel = options.groups[i].label;
                        break;
                    }
                }

                renderChips.push(
                    <div key={ `${id}-${group.key}` } className={ styles.chipGroup }>
                        <div className={ labelContainerClass }>
                            <span>{ groupLabel }</span>
                        </div>
                        <div className={ groupChipContainerClass }>
                            { chips[group.key] }
                        </div>
                    </div>
                );
            });
        } else {
            renderChips = chips;
        }

        return (
            <React.Fragment>
                <FormControl
                    required={ required }
                    error={ error }
                    component={ formControlComponent }
                    classes={{
                        root: `${classes.fieldSet || ''}`
                    }}
                >
                    <div className={ classes.labelWithHelperContainer }>
                        { title ? <FormLabel
                            component="legend"
                            classes={{
                                root: classes.titleContainer || ''
                            }}
                        >{ title }</FormLabel> : null }
                        { helperText ? <FormHelperText
                            classes={{
                                root: classes.helperContainer || ''
                            }}
                        >{ helperText }</FormHelperText> : null }
                    </div>
                    <div className={ `${styles.formGroup} ${classes.formGroup || ''}` }>
                        { renderChips }
                    </div>
                    { errorText ? <FormHelperText
                        classes={{
                            root: classes.errorContainer || ''
                        }}
                    >{ errorText }</FormHelperText> : null }
                </FormControl>
            </React.Fragment>
        );
    }
}

ChipSelectField.propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    error: PropTypes.bool,
    errorText: PropTypes.string,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    options: PropTypes.shape({
        groupLabelPlacement: PropTypes.oneOf(['top', 'left']),
        groups: PropTypes.arrayOf(PropTypes.shape({
            key: PropTypes.string,
            label: PropTypes.string
        }))
    }),
    valueOptions: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
        disabled: PropTypes.bool
    })),
    uiSchema: PropTypes.shape({
        classes: PropTypes.shape({
            fieldSet: PropTypes.string,
            formGroup: PropTypes.string,
            groupLabelContainer: PropTypes.string,
            groupChipContainer: PropTypes.string,
            titleContainer: PropTypes.string,
            helperContainer: PropTypes.string,
            errorContainer: PropTypes.string,
            input: PropTypes.string,
            chipContainer: PropTypes.string,
            labelContainer: PropTypes.string,
            chipLabelSelected: PropTypes.string,
            chipLabelDisabled: PropTypes.string,
            label: PropTypes.string,
            labelWithHelperContainer: PropTypes.string
        })
    }),
    inputValue: PropTypes.any,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    helperText: PropTypes.string,
    onChange: PropTypes.func,
    autoSelectSingleResult: PropTypes.bool,
    formControlComponent: PropTypes.string
};

ChipSelectField.defaultProps = {
    title: '',
    error: false,
    errorText: '',
    required: false,
    disabled: false,
    options: {
        groupLabelPlacement: 'top',
        groups: []
    },
    valueOptions: [],
    uiSchema: {},
    inputValue: [],
    helperText: '',
    formControlComponent: 'fieldset'
};
